import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import type { Tables, TablesUpdate } from '@/types/supabase'

const EDGE_API = import.meta.env.VITE_EDGE_API_URL ?? ''

export type Asset = Tables<'assets'>
export type TenantStorage = Tables<'tenant_storage'>
export type AssetFolder = Tables<'asset_folders'>

export type AssetFilter = 'orphaned' | 'in-use' | undefined

// ── useAssets ─────────────────────────────────────────────────────────────────
// folderId = undefined  → all assets (no folder filter)
// folderId = null       → root-only (folder_id IS NULL)
// folderId = 'uuid'     → assets in that specific folder

export function useAssets(filter?: AssetFilter, folderId?: string | null) {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['assets', tenantId, filter, folderId],
    enabled: !!tenantId,
    queryFn: async () => {
      let query = supabase
        .from('assets')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('created_at', { ascending: false })

      if (filter === 'orphaned') query = query.eq('usage_count', 0)
      else if (filter === 'in-use') query = query.gt('usage_count', 0)

      if (folderId === null) {
        query = query.is('folder_id', null)
      } else if (folderId) {
        query = query.eq('folder_id', folderId)
      }

      const { data, error } = await query
      if (error) throw error
      return data as Asset[]
    },
  })
}

// ── useStorageMetrics ─────────────────────────────────────────────────────────

export const STORAGE_QUOTA_BYTES = 5 * 1024 * 1024 * 1024 // 5 GB

export function useStorageMetrics() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['tenant-storage', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_storage')
        .select('*')
        .eq('tenant_id', tenantId!)
        .maybeSingle()

      if (error) throw error
      return (data as TenantStorage | null) ?? { tenant_id: tenantId!, bytes_used: 0 }
    },
  })
}

// ── useAssetFolderCounts ──────────────────────────────────────────────────────
// Distinct query key so it never collides with useAssets (which uses undefined/null
// params that JSON.stringify both serialize to null in arrays).

export function useAssetFolderCounts() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['asset-folder-counts', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assets')
        .select('id, folder_id')
        .eq('tenant_id', tenantId!)
        .not('folder_id', 'is', null)

      if (error) throw error
      return data as { id: string; folder_id: string }[]
    },
  })
}

// ── useAssetFolders ───────────────────────────────────────────────────────────

export function useAssetFolders() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['asset-folders', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('asset_folders')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('name', { ascending: true })

      if (error) throw error
      return data as AssetFolder[]
    },
  })
}

// ── useCreateFolder ───────────────────────────────────────────────────────────

export function useCreateFolder() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (name: string): Promise<AssetFolder> => {
      const { data, error } = await supabase
        .from('asset_folders')
        .insert({ tenant_id: tenantId!, name: name.trim() })
        .select()
        .single()

      if (error) throw error
      return data as AssetFolder
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asset-folders', tenantId] })
    },
  })
}

// ── useDeleteFolder ───────────────────────────────────────────────────────────
// Deleting a folder moves its assets to root (folder_id → null) via ON DELETE SET NULL.

export function useDeleteFolder() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('asset_folders').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['asset-folders', tenantId] })
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
    },
  })
}

// ── useUploadAsset ────────────────────────────────────────────────────────────
// Sends multipart/form-data to POST /assets/upload → Worker → ImageKit → DB.

export interface UploadAssetInput {
  file: File
  alt_text?: string
  caption?: string
  tags?: string      // comma-separated, e.g. "member,concert,2025"
  folder_id?: string // UUID of the target folder (omit for root)
}

export function useUploadAsset() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const session = useAuthStore((s) => s.session)

  return useMutation({
    mutationFn: async (input: UploadAssetInput): Promise<Asset> => {
      if (!session?.access_token) throw new Error('Not authenticated')

      const form = new FormData()
      form.append('file', input.file)
      if (input.alt_text) form.append('alt_text', input.alt_text)
      if (input.caption) form.append('caption', input.caption)
      if (input.tags) form.append('tags', input.tags)
      if (input.folder_id) form.append('folder_id', input.folder_id)

      // Do NOT set Content-Type — browser sets it automatically with boundary
      const res = await fetch(`${EDGE_API}/assets/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: form,
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? 'Upload failed')
      }

      return res.json() as Promise<Asset>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
      qc.invalidateQueries({ queryKey: ['tenant-storage', tenantId] })
    },
  })
}

// ── useUpdateAsset ────────────────────────────────────────────────────────────

export function useUpdateAsset() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({
      id,
      ...update
    }: TablesUpdate<'assets'> & { id: string }): Promise<Asset> => {
      const { data, error } = await supabase
        .from('assets')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Asset
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
    },
  })
}

// ── useDeleteAsset ────────────────────────────────────────────────────────────

export function useDeleteAsset() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('assets').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
      qc.invalidateQueries({ queryKey: ['tenant-storage', tenantId] })
    },
  })
}

// ── usePurgeAssets ────────────────────────────────────────────────────────────
// Calls the edge API (needs JWT) which handles both ImageKit + DB cleanup.

export function usePurgeAssets() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const session = useAuthStore((s) => s.session)

  return useMutation({
    mutationFn: async (): Promise<{ deleted: number }> => {
      if (!session?.access_token) throw new Error('Not authenticated')

      const res = await fetch(`${EDGE_API}/assets/purge`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? 'Purge failed')
      }

      return res.json() as Promise<{ deleted: number }>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
      qc.invalidateQueries({ queryKey: ['tenant-storage', tenantId] })
    },
  })
}

// ── useAdjustAssetUsage ───────────────────────────────────────────────────────
// Client-side usage tracking for assets referenced in JSONB block settings.
// The DB trigger only fires on FK-based asset_id columns; JSONB references
// (e.g. page block settings) need explicit increment/decrement from the client.

export function useAdjustAssetUsage() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ assetId, delta }: { assetId: string; delta: 1 | -1 }) => {
      const { data: asset, error: fetchError } = await supabase
        .from('assets')
        .select('usage_count')
        .eq('id', assetId)
        .single()

      if (fetchError) throw fetchError

      const newCount = Math.max(0, (asset.usage_count ?? 0) + delta)
      const { error } = await supabase
        .from('assets')
        .update({ usage_count: newCount })
        .eq('id', assetId)

      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets', tenantId] })
    },
  })
}

// ── preflightCheck ────────────────────────────────────────────────────────────

export interface PreflightResult {
  allowed: boolean
  bytes_used: number
  quota: number
  message?: string
}

export async function preflightCheck(
  fileSize: number,
  accessToken: string,
): Promise<PreflightResult> {
  const res = await fetch(`${EDGE_API}/assets/pre-upload-check`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ file_size: fileSize }),
  })

  if (!res.ok) throw new Error('Pre-upload check request failed')
  return res.json() as Promise<PreflightResult>
}
